/**
 * `<Input />` — Story 5.4 (UX §4.2 lines 892-899, AC line 1007).
 *
 * Five-`type` text-input wrapper with two label modes (top-aligned
 * default + opt-in floating). Consumed by Story 6.5 event-create
 * wizard (color + datetime + text + email) and Story 8.6 submitter
 * upload (file).
 *
 * Type variants:
 *   - `text`            — default text input.
 *   - `email`           — text input with `autoComplete="email"` + `inputMode="email"`.
 *   - `datetime-local`  — native HTML datetime-local. AC line 1007 says
 *                         "datetime"; we lock to the HTML standard.
 *   - `color`           — native HTML color picker (Story 6.5).
 *   - `file`            — native HTML file input (Story 8.6).
 *
 * Label modes (UX §4.2 line 894):
 *   - `top` (default)   — label rendered above the input.
 *   - `floating`        — label initially overlaid; floats up on focus or
 *                         when the input has a value.
 *
 * Helper / error / required (UX §4.2 lines 895-897):
 *   - `helperText` — muted text 4px below.
 *   - `errorText`  — `--danger` text 4px below; toggles
 *                    `aria-invalid="true"` + `aria-describedby` linkage.
 *   - `required`   — visible asterisk in `--danger` + native attr.
 *
 * Focus-ring contract per Critical Scope Decision #6 (full
 * `outlineWidth/outlineStyle/outlineColor/outlineOffset`).
 *
 * NOT shipped in this story:
 *   - `<select>` — UX §4.1 doesn't list a dropdown primitive.
 *   - Custom datetime popover — UX §4.2 line 899 mentions a desktop
 *     popover; Story 5.4 ships native only. Story 6.5 may layer a popover.
 */
import { useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';

export type InputType = 'text' | 'email' | 'datetime-local' | 'color' | 'file';

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  type: InputType;
  /** Required label text. Pass `""` plus `aria-label` for the rare label-less case. */
  label: string;
  labelMode?: 'top' | 'floating';
  helperText?: string;
  errorText?: string;
}

const TAP_TARGET_MIN_PX = 44;

function autoCompleteFor(type: InputType): string | undefined {
  if (type === 'email') return 'email';
  return undefined;
}

function inputModeFor(type: InputType): InputHTMLAttributes<HTMLInputElement>['inputMode'] {
  if (type === 'email') return 'email';
  return undefined;
}

/**
 * `Input` — accessible labeled text input with five HTML type variants.
 *
 * @param type        One of `text | email | datetime-local | color | file`.
 * @param label       Required; visible label.
 * @param labelMode   `top` (default) or `floating`.
 * @param helperText  Optional muted helper.
 * @param errorText   Optional error; sets aria-invalid + aria-describedby.
 * @param required    Native attribute; renders a red asterisk.
 */
const FLOATING_LABEL_KEYFRAMES = `
.pvt-input-floating-wrap > .pvt-input-floating-label {
  position: absolute;
  left: var(--space-3);
  top: 14px;
  font-size: var(--text-base-size);
  color: var(--text-muted);
  pointer-events: none;
  background: var(--bg-surface);
  padding: 0 var(--space-1);
  transition: top 120ms ease-out, font-size 120ms ease-out, color 120ms ease-out;
}
.pvt-input-floating-wrap > input:focus + .pvt-input-floating-label,
.pvt-input-floating-wrap > input:not(:placeholder-shown) + .pvt-input-floating-label {
  top: -8px;
  font-size: var(--text-xs-size);
  color: var(--text-secondary);
}
@media (prefers-reduced-motion: reduce) {
  .pvt-input-floating-wrap > .pvt-input-floating-label {
    transition: none;
  }
}
`;

export function Input({
  type,
  label,
  labelMode = 'top',
  helperText,
  errorText,
  required,
  id: idProp,
  style,
  placeholder,
  ...rest
}: InputProps) {
  const generatedId = useId();
  const inputId = idProp ?? generatedId;
  const helperId = `${inputId}-helper`;
  const errorId = `${inputId}-error`;
  const hasError = Boolean(errorText);

  const describedBy = [
    helperText ? helperId : null,
    hasError ? errorId : null,
  ]
    .filter(Boolean)
    .join(' ');

  const labelContent: ReactNode = (
    <>
      {label}
      {required && (
        <span aria-hidden="true" style={{ color: 'var(--danger)', marginLeft: 4 }}>
          *
        </span>
      )}
    </>
  );

  const topLabelNode: ReactNode = (
    <label
      htmlFor={inputId}
      style={{ fontSize: 'var(--text-sm-size)', color: 'var(--text-secondary)' }}
    >
      {labelContent}
    </label>
  );

  // Floating-label mode: the visible label is rendered AFTER the input as
  // a `<label>` so the `:placeholder-shown` and `:focus` adjacent-sibling
  // CSS selectors can drive the float animation. The `<label htmlFor>`
  // wires the accessible name (H8 fix). A space placeholder makes
  // `:placeholder-shown` reflect "input is empty" (UX §4.2 line 894).
  const floatingPlaceholder = labelMode === 'floating' ? (placeholder ?? ' ') : placeholder;

  const inputNode = (
    <input
      {...rest}
      id={inputId}
      type={type}
      required={required}
      placeholder={floatingPlaceholder}
      autoComplete={rest.autoComplete ?? autoCompleteFor(type)}
      inputMode={rest.inputMode ?? inputModeFor(type)}
      aria-invalid={hasError || undefined}
      aria-describedby={describedBy || undefined}
      data-label-mode={labelMode}
      style={{
        minHeight: TAP_TARGET_MIN_PX,
        padding: '0 var(--space-3)',
        background: 'var(--bg-surface)',
        color: 'var(--text-primary)',
        border: `1px solid ${hasError ? 'var(--danger)' : 'var(--border-strong)'}`,
        borderRadius: 'var(--radius-md)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-base-size)',
        outlineWidth: 2,
        outlineStyle: 'solid',
        outlineColor: 'var(--brand-primary)',
        outlineOffset: 2,
        ...style,
      }}
    />
  );

  return (
    <div
      data-label-mode={labelMode}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-1)',
      }}
    >
      {labelMode === 'top' ? topLabelNode : null}
      {labelMode === 'floating' ? (
        <>
          <style>{FLOATING_LABEL_KEYFRAMES}</style>
          <div className="pvt-input-floating-wrap" style={{ position: 'relative' }}>
            {inputNode}
            <label
              htmlFor={inputId}
              data-testid="floating-label"
              className="pvt-input-floating-label"
            >
              {labelContent}
            </label>
          </div>
        </>
      ) : (
        inputNode
      )}
      {helperText && (
        <p
          id={helperId}
          style={{
            margin: 0,
            marginTop: 4,
            fontSize: 'var(--text-xs-size)',
            color: 'var(--text-muted)',
          }}
        >
          {helperText}
        </p>
      )}
      {hasError && (
        <p
          id={errorId}
          role="alert"
          style={{
            margin: 0,
            marginTop: 4,
            fontSize: 'var(--text-xs-size)',
            color: 'var(--danger)',
          }}
        >
          {errorText}
        </p>
      )}
    </div>
  );
}
